/**
 * @component ry-field
 * @category  form
 * @summary   Form-field wrapper around any native `<input>`, `<textarea>`, or `<select>` (or compatible custom control). Provides label + hint + error/success/warning + addon icons + action button + size + width + required + loading + disabled wiring with full Fomantic parity.
 *
 * Reference: Fomantic UI's `.field` (inside `.ui.form`). ry-field collapses
 * Fomantic's label + hint + error + size + state + addon machinery into a
 * single attribute-driven wrapper.
 *
 * @attribute label       string?    Label text. When set, a `<label for="...">` is inserted before the input.
 * @attribute hint        string?    Subtle helper text rendered below the input. Cleared when null/missing.
 * @attribute error       string?    Error message. Toggles `aria-invalid="true"` on the inner control. Visually drives danger border + icon.
 * @attribute success     string?    Success message. Visually drives success border + checkmark icon.
 * @attribute warning     string?    Warning message. Visually drives warning border + warning icon.
 * @attribute required    boolean    Adds a visible asterisk to the label and sets `aria-required="true"` on the inner control.
 * @attribute disabled    boolean    Propagates `disabled` to the inner control.
 * @attribute loading     boolean    Shows a spinner addon (replaces trailing icon if any), sets `aria-busy="true"`, dims interaction.
 * @attribute inline      boolean    Label-on-left layout instead of label-above.
 * @attribute size        string?    mini | tiny | small | medium | large | big | huge | massive — font-size scaled by --ry-space-scale.
 * @attribute width       string?    1..16 — flex-basis token. Only meaningful inside <ry-fields>.
 *
 * @content   Exactly one form control as a child: `<input>`, `<textarea>`, `<select>`, or a ry-* form custom element (e.g. `<ry-checkbox>`, `<ry-select>`). Optional addon children with `slot="leading"` (icon), `slot="trailing"` (icon), or `slot="action"` (button) are detected and laid out flush.
 *
 * @dom
 *   ry-field[label?][error?][hint?][success?][warning?][required?][loading?][inline?][size?][width?]
 *     > label.ry-label    [data-ry-target="label"] for=input-id    (only when [label])
 *       > "label-text"
 *       > span[data-ry-target="required-marker"]?         (only when [required])
 *     > div[data-ry-target="control"].ry-field__control
 *       > [slot="leading"]  (optional addon icon)
 *       > {input | textarea | select | ry-*}              (the inner control)
 *       > [slot="trailing"] (optional addon icon — replaced by spinner when [loading])
 *       > [slot="action"]   (optional action button)
 *     > div.ry-field__error    [data-ry-target="error"]    role="alert"  (one of error/success/warning text)
 *     > div.ry-field__hint       [data-ry-target="hint"]       id="input-id-hint"  (subtle hint)
 *
 * @gotcha  Setup runs once. The wrapper attaches DOM nodes ONCE — if the inner input changes (replaced after mount, or wrapped element-swap), the label/hint/error linkage will not update. Keep the input stable for the field's lifetime.
 *
 * @gotcha  `error`, `success`, `warning` are mutually exclusive in terms of MESSAGE display: only one message slot is rendered. The most recently set wins; clearing one falls back to the next set state. ARIA-invalid is only toggled by `[error]`.
 *
 * @gotcha  `[width]` is only meaningful when the field is a direct child of `<ry-fields>`. Used standalone, it sets flex-basis but the field is block-level so nothing visible changes unless you're in a flex row.
 *
 * @gotcha  `[loading]` replaces the trailing addon (if any) with a spinner. Restoring `[loading]=false` re-shows the original addon. The trailing slot's original DOM is preserved.
 *
 * @gotcha  `[size]` scales font-size and the inner control inherits via `em`. Set on the field, NOT on the inner input.
 *
 * @example basic
 * ```html
 * <ry-field label="Email">
 *   <input type="email" name="email" placeholder="you@example.com">
 * </ry-field>
 * ```
 *
 * @example with-hint
 * ```html
 * <ry-field label="Password" hint="Minimum 8 characters">
 *   <input type="password" name="password">
 * </ry-field>
 * ```
 *
 * @example required
 * ```html
 * <ry-field label="Username" required>
 *   <input type="text" name="username">
 * </ry-field>
 * ```
 *
 * @example with-error
 * ```html
 * <ry-field label="Username" error="Already taken">
 *   <input type="text" name="username" value="taken_name">
 * </ry-field>
 * ```
 *
 * @example success-state
 * ```html
 * <ry-field label="Slug" success="Available!">
 *   <input type="text" name="slug" value="my-cool-app">
 * </ry-field>
 * ```
 *
 * @example warning-state
 * ```html
 * <ry-field label="Password" warning="Weak — consider adding a number">
 *   <input type="password" name="pw" value="apple">
 * </ry-field>
 * ```
 *
 * @example loading
 * ```html
 * <ry-field label="Checking availability" loading>
 *   <input type="text" name="slug" value="…">
 * </ry-field>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-field label="Username" disabled>
 *   <input type="text" value="locked">
 * </ry-field>
 * ```
 *
 * @example inline
 * ```html
 * <ry-field label="Name" inline>
 *   <input type="text">
 * </ry-field>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-field label="Mini"   size="mini"><input type="text"></ry-field>
 * <ry-field label="Small"  size="small"><input type="text"></ry-field>
 * <ry-field label="Large"  size="large"><input type="text"></ry-field>
 * <ry-field label="Huge"   size="huge"><input type="text"></ry-field>
 * ```
 *
 * @example with-leading-icon
 * ```html
 * <ry-field label="Email">
 *   <ry-icon slot="leading" name="mail"></ry-icon>
 *   <input type="email">
 * </ry-field>
 * ```
 *
 * @example with-action-button
 * ```html
 * <ry-field label="Search">
 *   <input type="text" placeholder="Search…">
 *   <ry-button slot="action" variant="primary">Go</ry-button>
 * </ry-field>
 * ```
 *
 * @tutorial mount-wires-label-to-input
 * ```js
 * const el = mount('<ry-field label="Email"><input type="email"></ry-field>');
 * const label = el.querySelector('[data-ry-target="label"]');
 * const input = el.querySelector('input');
 * await expectEqual(input.id.length > 0, true, 'input got an auto-generated id');
 * await expectAttr(label, 'for', input.id, 'label[for] matches input id');
 * ```
 *
 * @tutorial required-marker-renders
 * ```js
 * const el = mount('<ry-field label="Name" required><input></ry-field>');
 * const marker = el.querySelector('[data-ry-target="required-marker"]');
 * await expectEqual(!!marker, true, 'required marker rendered');
 * const input = el.querySelector('input');
 * await expectAttr(input, 'aria-required', 'true');
 * ```
 *
 * @tutorial disabled-propagates
 * ```js
 * const el = mount('<ry-field label="x" disabled><input></ry-field>');
 * const input = el.querySelector('input');
 * await expectEqual(input.disabled, true, 'disabled propagated to input');
 * ```
 *
 * @tutorial loading-aria-busy
 * ```js
 * const el = mount('<ry-field label="x" loading><input></ry-field>');
 * const input = el.querySelector('input');
 * await expectAttr(input, 'aria-busy', 'true', 'aria-busy set');
 * el.removeAttribute('loading');
 * await expectEqual(input.hasAttribute('aria-busy'), false, 'aria-busy removed');
 * ```
 *
 * @tutorial state-message-success
 * ```js
 * const el = mount('<ry-field label="x" success="Looks good!"><input></ry-field>');
 * const msg = el.querySelector('[data-ry-target="error"]');
 * await expectEqual(msg.textContent, 'Looks good!');
 * await expectEqual(el.getAttribute('data-ry-state'), 'success');
 * ```
 *
 * @see ry-form, ry-fields
 */

import { RyElement } from '../core/ry-element.js';

type FieldState = 'error' | 'success' | 'warning' | null;

const STATE_ATTRS = ['error', 'success', 'warning'] as const;

export class RyField extends RyElement {
  static get observedAttributes(): string[] {
    return [
      'label', 'error', 'hint', 'success', 'warning',
      'required', 'disabled', 'loading', 'size', 'inline', 'width',
    ];
  }

  #savedTrailing: Element | null = null;

  setup(): void {
    this.#transformMarkup();
    this.#syncRequired();
    this.#syncDisabled();
    this.#syncLoading();
    this.#syncState();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;

    if (name === 'label') {
      this.#updateLabel(newValue);
    } else if (name === 'hint') {
      this.#updateHint(newValue);
    } else if (name === 'error' || name === 'success' || name === 'warning') {
      this.#syncState();
    } else if (name === 'required') {
      this.#syncRequired();
    } else if (name === 'disabled') {
      this.#syncDisabled();
    } else if (name === 'loading') {
      this.#syncLoading();
    }
  }

  /** Resolve the inner control (native form element OR a known ry- form custom element). */
  #control(): HTMLElement | null {
    return this.querySelector<HTMLElement>(
      'input, textarea, select, ry-checkbox, ry-radio, ry-switch, ry-select, ry-combobox, ry-tag-input, ry-slider, ry-rating, ry-color-input, ry-number-select'
    );
  }

  #transformMarkup(): void {
    const input = this.#control();
    if (!input) return;

    // Generate ID if needed (native controls only — custom elements may have their own scheme)
    if (!input.id) {
      input.id = `ry-field-${Math.random().toString(36).slice(2, 9)}`;
    }
    const inputId = input.id;

    // Wrap the control in a [data-ry-target="control"] container so addons can sit alongside
    let controlEl = this.$<HTMLElement>('[data-ry-target="control"]');
    if (!controlEl) {
      controlEl = document.createElement('div');
      controlEl.setAttribute('data-ry-target', 'control');
      controlEl.className = 'ry-field__control';
      input.parentNode?.insertBefore(controlEl, input);
      // Move addons (leading, trailing, action) + input into the control wrapper
      const leading = this.querySelector<HTMLElement>(':scope > [slot="leading"]');
      const trailing = this.querySelector<HTMLElement>(':scope > [slot="trailing"]');
      const action = this.querySelector<HTMLElement>(':scope > [slot="action"]');
      if (leading) {
        leading.setAttribute('data-ry-target', 'leading');
        controlEl.appendChild(leading);
      }
      controlEl.appendChild(input);
      if (trailing) {
        trailing.setAttribute('data-ry-target', 'trailing');
        controlEl.appendChild(trailing);
      }
      if (action) {
        action.setAttribute('data-ry-target', 'action');
        controlEl.appendChild(action);
      }
      // Flag for CSS layout
      if (leading) this.setAttribute('data-ry-has-leading', '');
      if (trailing) this.setAttribute('data-ry-has-trailing', '');
      if (action) this.setAttribute('data-ry-has-action', '');
    }

    // Label
    if (!this.$('[data-ry-target="label"]')) {
      const labelText = this.getAttribute('label');
      if (labelText) {
        const label = document.createElement('label');
        label.setAttribute('data-ry-target', 'label');
        label.className = 'ry-label';
        label.setAttribute('for', inputId);
        label.textContent = labelText;
        this.insertBefore(label, controlEl);
      }
    }

    // Message slot — single slot, holds error/success/warning text. Keyed by data-ry-target="error" for back-compat.
    if (!this.$('[data-ry-target="error"]')) {
      const msgEl = document.createElement('div');
      msgEl.setAttribute('data-ry-target', 'error');
      msgEl.className = 'ry-field__error';
      msgEl.setAttribute('role', 'alert');
      controlEl.insertAdjacentElement('afterend', msgEl);
    }

    // Hint
    if (!this.$('[data-ry-target="hint"]')) {
      const hintEl = document.createElement('div');
      hintEl.setAttribute('data-ry-target', 'hint');
      hintEl.className = 'ry-field__hint';
      hintEl.id = `${inputId}-hint`;
      const hintText = this.getAttribute('hint');
      if (hintText) hintEl.textContent = hintText;
      const msgEl = this.$('[data-ry-target="error"]');
      (msgEl ?? controlEl).insertAdjacentElement('afterend', hintEl);
    }

    // Assign ids + aria-describedby
    const msgEl = this.$('[data-ry-target="error"]');
    const hintEl = this.$('[data-ry-target="hint"]');
    if (msgEl) msgEl.id = `${inputId}-error`;
    if (hintEl) hintEl.id = `${inputId}-hint`;
    const describedBy = [msgEl?.id, hintEl?.id].filter(Boolean) as string[];
    if (describedBy.length) {
      input.setAttribute('aria-describedby', describedBy.join(' '));
    }
  }

  #updateLabel(text: string | null): void {
    const labelEl = this.$('[data-ry-target="label"]');
    if (!labelEl) {
      // Late-set label: render it now if we have a control
      const input = this.#control();
      if (text && input) {
        const label = document.createElement('label');
        label.setAttribute('data-ry-target', 'label');
        label.className = 'ry-label';
        label.setAttribute('for', input.id);
        label.textContent = text;
        const controlEl = this.$('[data-ry-target="control"]');
        this.insertBefore(label, controlEl ?? input);
        this.#syncRequired();
      }
      return;
    }
    // Preserve required marker if present
    const marker = labelEl.querySelector('[data-ry-target="required-marker"]');
    labelEl.textContent = text ?? '';
    if (marker) labelEl.appendChild(marker);
    this.#syncRequired();
  }

  #updateHint(text: string | null): void {
    const el = this.$('[data-ry-target="hint"]');
    if (el) el.textContent = text ?? '';
  }

  #syncRequired(): void {
    const required = this.hasAttribute('required');
    const input = this.#control();
    if (input) {
      if (required) input.setAttribute('aria-required', 'true');
      else input.removeAttribute('aria-required');
    }
    const labelEl = this.$('[data-ry-target="label"]');
    if (!labelEl) return;
    let marker = labelEl.querySelector('[data-ry-target="required-marker"]');
    if (required && !marker) {
      marker = document.createElement('span');
      marker.setAttribute('data-ry-target', 'required-marker');
      marker.setAttribute('aria-hidden', 'true');
      marker.className = 'ry-field__required';
      marker.textContent = '*';
      labelEl.appendChild(marker);
    } else if (!required && marker) {
      marker.remove();
    }
  }

  #syncDisabled(): void {
    const disabled = this.hasAttribute('disabled');
    const input = this.#control();
    if (!input) return;
    if (disabled) {
      input.setAttribute('disabled', '');
      (input as HTMLInputElement).disabled = true;
    } else {
      input.removeAttribute('disabled');
      (input as HTMLInputElement).disabled = false;
    }
  }

  #syncLoading(): void {
    const loading = this.hasAttribute('loading');
    const input = this.#control();
    if (input) {
      if (loading) input.setAttribute('aria-busy', 'true');
      else input.removeAttribute('aria-busy');
    }
    const controlEl = this.$('[data-ry-target="control"]');
    if (!controlEl) return;
    const existingSpinner = controlEl.querySelector('[data-ry-target="spinner"]');
    if (loading) {
      if (existingSpinner) return;
      // Hide the existing trailing addon if any
      const trailing = controlEl.querySelector(':scope > [data-ry-target="trailing"]');
      if (trailing) {
        this.#savedTrailing = trailing;
        trailing.remove();
      }
      const spinner = document.createElement('span');
      spinner.setAttribute('data-ry-target', 'spinner');
      spinner.setAttribute('aria-hidden', 'true');
      spinner.className = 'ry-field__spinner';
      controlEl.appendChild(spinner);
    } else {
      existingSpinner?.remove();
      if (this.#savedTrailing) {
        controlEl.appendChild(this.#savedTrailing);
        this.#savedTrailing = null;
      }
    }
  }

  /** Render whichever state message is set (error > warning > success priority is consumer's call — last-write wins by precedence: error > warning > success). */
  #syncState(): void {
    let state: FieldState = null;
    let text: string | null = null;
    if (this.hasAttribute('error')) {
      state = 'error';
      text = this.getAttribute('error');
    } else if (this.hasAttribute('warning')) {
      state = 'warning';
      text = this.getAttribute('warning');
    } else if (this.hasAttribute('success')) {
      state = 'success';
      text = this.getAttribute('success');
    }
    const msgEl = this.$('[data-ry-target="error"]');
    if (msgEl) msgEl.textContent = text ?? '';

    // data-ry-state holds the visual state hint for theme CSS
    if (state) this.setAttribute('data-ry-state', state);
    else this.removeAttribute('data-ry-state');

    // aria-invalid only mirrors error
    const input = this.#control();
    if (input) {
      if (state === 'error' && text) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  /** Read the current value of the wrapped control (best-effort across native + ry-* form elements). */
  get value(): string {
    const input = this.#control();
    if (!input) return '';
    if ('value' in input) return String((input as HTMLInputElement).value ?? '');
    return '';
  }

  set value(val: string) {
    const input = this.#control();
    if (!input) return;
    if ('value' in input) (input as HTMLInputElement).value = val;
  }

  /** Get the inner control's name attribute. */
  get name(): string {
    const input = this.#control();
    return input?.getAttribute('name') ?? '';
  }

  /** Programmatically set the field's state-message. */
  setState(state: 'error' | 'success' | 'warning' | 'none', message?: string): void {
    // Clear all three first
    for (const s of STATE_ATTRS) this.removeAttribute(s);
    if (state === 'none') return;
    this.setAttribute(state, message ?? '');
  }
}

customElements.define('ry-field', RyField);
